import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreHorizontal, Edit, Trash2, Check, X, AlertCircle } from 'lucide-react';

import { Button } from "../ui/button";
import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../ui/card";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu.tsx";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    Checkbox
} from "../ui/checkbox";
import {
    Badge
} from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { useApi } from "../../lib/ApiContext";

const UsersIndex = () => {
    const [users, setUsers] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [selectedOrgs, setSelectedOrgs] = useState([]);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserName, setNewUserName] = useState('');
    const [validationError, setValidationError] = useState('');
    const navigate = useNavigate();
    const api = useApi();

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch users and organizations in parallel
                const [usersData, orgsData] = await Promise.all([
                    api.users.getAll(),
                    api.organizations.getAll()
                ]);

                setUsers(usersData);
                setOrganizations(orgsData);
                setLoading(false);
            } catch (err) {
                console.error("Failed to fetch data:", err);
                setError(err.message);
                setLoading(false);
            }
        };

        fetchData();
    }, [api.users, api.organizations]);

    const handleEditUser = (user) => {
        // Store the user data before opening the dialog
        setSelectedUser(user);

        // Extract organization IDs from user.organizations array
        const userOrgIds = user.organizations ? user.organizations.map(org => org.id) : [];
        setSelectedOrgs(userOrgIds);
        setIsSuperAdmin(user.is_super_admin === 1);

        // Open the dialog directly
        setEditDialogOpen(true);
    };

    const handleRemoveUser = async (userId) => {
        if (window.confirm("Are you sure you want to remove this user?")) {
            try {
                await api.users.delete(userId);
                // Update the local state to remove the user
                setUsers(users.filter(user => user.id !== userId));
            } catch (err) {
                console.error("Failed to delete user:", err);
                alert(`Error: ${err.message}`);
            }
        }
    };

    const handleSaveUserChanges = async () => {
        if (!selectedUser) return;

        try {
            // Prepare the update data
            const updateData = {
                name: selectedUser.name,
                email: selectedUser.email,
                status: selectedUser.status,
                is_super_admin: isSuperAdmin ? 1 : 0,
                organizations: selectedOrgs
            };

            // Update the user
            const updatedUser = await api.users.update(selectedUser.id, updateData);

            // Update the local state
            setUsers(users.map(user =>
                user.id === updatedUser.id ? updatedUser : user
            ));

            // Close the dialog
            setEditDialogOpen(false);
        } catch (err) {
            console.error("Failed to update user:", err);
            alert(`Error: ${err.message}`);
        }
    };

    const handleAddUser = () => {
        // Open add user dialog and reset form fields
        setNewUserEmail('');
        setNewUserName('');
        setSelectedOrgs([]);
        setIsSuperAdmin(false);
        setValidationError('');
        setAddDialogOpen(true);
    };

    const handleCreateUser = async () => {
        if (!newUserEmail || !newUserName) return;

        // Clear any previous validation errors
        setValidationError('');

        // Validate that non-super admin users have at least one organization selected
        if (!isSuperAdmin && selectedOrgs.length === 0) {
            setValidationError('Please select at least one organization for this user.');
            return;
        }

        try {
            // Create a new user with the provided details
            const newUserData = {
                name: newUserName,
                email: newUserEmail,
                status: "invited", // New users start as invited
                is_super_admin: isSuperAdmin ? 1 : 0,
                organizations: selectedOrgs
            };

            // Send the data to the API
            const createdUser = await api.users.create(newUserData);

            // Update the local state
            setUsers([...users, createdUser]);

            // Close the dialog
            setAddDialogOpen(false);

            // Reset form
            setNewUserEmail('');
            setNewUserName('');
            setSelectedOrgs([]);
            setIsSuperAdmin(false);
            setValidationError('');
        } catch (err) {
            console.error("Failed to create user:", err);
            alert(`Error: ${err.message}`);
        }
    };

    const toggleOrganization = (orgId) => {
        if (selectedOrgs.includes(orgId)) {
            setSelectedOrgs(selectedOrgs.filter(id => id !== orgId));
        } else {
            setSelectedOrgs([...selectedOrgs, orgId]);
        }
    };

    const renderOrganizationCheckboxes = () => {
        return (
            <div className="space-y-2 max-h-[200px] overflow-y-auto border rounded-md p-2">
                {organizations.map((org) => (
                    <div key={org.id} className="flex items-center space-x-2">
                        <Checkbox
                            id={`org-${org.id}`}
                            checked={selectedOrgs.includes(org.id)}
                            onCheckedChange={() => toggleOrganization(org.id)}
                            disabled={isSuperAdmin}
                        />
                        <label
                            htmlFor={`org-${org.id}`}
                            className={`text-sm font-medium leading-none ${isSuperAdmin ? 'opacity-50' : ''}`}
                        >
                            {org.name}
                        </label>
                    </div>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="container mx-auto p-4">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900" data-testid="loading-spinner"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto p-4">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>
                        Failed to load data: {error}
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">Users</h1>
                <Button onClick={handleAddUser}>
                    Add User
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>All Users</CardTitle>
                    <CardDescription>
                        Manage users and their access to organizations.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableCaption>List of all users in the system.</TableCaption>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Organizations</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Role</TableHead>
                                <TableHead className="w-[50px]"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                                        No users found. Click "Add User" to create one.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                users.map((user) => (
                                    <TableRow key={user.id}>
                                        <TableCell className="font-medium">{user.name}</TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>
                                            {user.is_super_admin === 1 ? (
                                                <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                                                    All
                                                </Badge>
                                            ) : (
                                                <div className="flex flex-wrap gap-1">
                                                    {user.organizations && user.organizations.length > 0 ? (
                                                        user.organizations.map((org) => (
                                                            <Badge key={org.id} variant="outline">
                                                                {org.name}
                                                            </Badge>
                                                        ))
                                                    ) : (
                                                        <span className="text-gray-400 text-sm">None</span>
                                                    )}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant={user.status === "active" ? "default" : "secondary"}
                                            >
                                                {user.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {user.is_super_admin === 1 ? (
                                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                                    Super Admin
                                                </Badge>
                                            ) : (
                                                <span className="text-gray-400 text-sm">User</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger>
                                                    <div
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md p-0 text-sm font-medium transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer"
                                                    >
                                                        <MoreHorizontal className="h-4 w-4" />
                                                        <span className="sr-only">Actions</span>
                                                    </div>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onSelect={(e) => {
                                                        e.preventDefault();
                                                        handleEditUser(user);
                                                    }}>
                                                        <Edit className="mr-2 h-4 w-4" />
                                                        <span>Edit</span>
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onSelect={() => handleRemoveUser(user.id)}>
                                                        <Trash2 className="mr-2 h-4 w-4" />
                                                        <span>Remove</span>
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Edit User Dialog */}
            <Dialog
                open={editDialogOpen}
                onOpenChange={(open) => setEditDialogOpen(open)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit User Access</DialogTitle>
                        <DialogDescription>
                            Modify the user's organization access and permissions.
                        </DialogDescription>
                    </DialogHeader>

                    {selectedUser && (
                        <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="name" className="text-right">
                                    Name
                                </Label>
                                <div className="col-span-3">
                                    <p>{selectedUser.name}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="email" className="text-right">
                                    Email
                                </Label>
                                <div className="col-span-3">
                                    <p>{selectedUser.email}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label className="text-right">
                                    Super Admin
                                </Label>
                                <div className="col-span-3 flex items-center space-x-2">
                                    <Checkbox
                                        id="super-admin"
                                        checked={isSuperAdmin}
                                        onCheckedChange={setIsSuperAdmin}
                                    />
                                    <label htmlFor="super-admin" className="text-sm text-gray-600">
                                        Grant super admin access to all organizations
                                    </label>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-start gap-4">
                                <Label className="text-right mt-2">
                                    Organizations
                                </Label>
                                <div className="col-span-3">
                                    {renderOrganizationCheckboxes()}
                                    {isSuperAdmin && (
                                        <p className="text-sm text-gray-500 mt-2">
                                            Super admins have access to all organizations
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <DialogFooter className="flex gap-2 justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setEditDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSaveUserChanges}
                        >
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add User Dialog */}
            <Dialog
                open={addDialogOpen}
                onOpenChange={(open) => setAddDialogOpen(open)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add New User</DialogTitle>
                        <DialogDescription>
                            Invite a new user to the system.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="new-name" className="text-right">
                                Name
                            </Label>
                            <Input
                                id="new-name"
                                value={newUserName}
                                onChange={(e) => setNewUserName(e.target.value)}
                                className="col-span-3"
                            />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="new-email" className="text-right">
                                Email
                            </Label>
                            <Input
                                id="new-email"
                                type="email"
                                value={newUserEmail}
                                onChange={(e) => setNewUserEmail(e.target.value)}
                                className="col-span-3"
                            />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right">
                                Super Admin
                            </Label>
                            <div className="col-span-3 flex items-center space-x-2">
                                <Checkbox
                                    id="new-super-admin"
                                    checked={isSuperAdmin}
                                    onCheckedChange={(checked) => {
                                        setIsSuperAdmin(checked);
                                        // Clear validation error when toggling super admin
                                        if (checked) {
                                            setValidationError('');
                                        }
                                    }}
                                />
                                <label htmlFor="new-super-admin" className="text-sm text-gray-600">
                                    Grant super admin access to all organizations
                                </label>
                            </div>
                        </div>
                        <div className="grid grid-cols-4 items-start gap-4">
                            <Label className="text-right mt-2">
                                Organizations
                            </Label>
                            <div className="col-span-3">
                                {validationError && (
                                    <Alert variant="destructive" className="mb-3">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>
                                            {validationError}
                                        </AlertDescription>
                                    </Alert>
                                )}
                                {renderOrganizationCheckboxes()}
                                {isSuperAdmin && (
                                    <p className="text-sm text-gray-500 mt-2">
                                        Super admins have access to all organizations
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="flex gap-2 justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setAddDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleCreateUser}
                        >
                            Create User
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UsersIndex; 